/**
 * Thin client for the kip-backend `/pwa/*` endpoints.
 *
 * Everything is same-origin (the backend serves this bundle), so the `kip_pwa`
 * session cookie rides along on its own — we just need `credentials: 'include'`
 * for the dev proxy case. Errors come back in the OpenAI-ish
 * `{ error: { message, type, code } }` shape the backend uses everywhere.
 */

export interface ApiErrorBody {
  error?: { message?: string; type?: string; code?: string };
  retryAfter?: number;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly type: string;
  readonly retryAfter: number | undefined;

  constructor(status: number, body: ApiErrorBody = {}) {
    super(body.error?.message || `Request failed (${status})`);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.error?.code || (status === 0 ? 'offline' : 'error');
    this.type = body.error?.type || 'error';
    this.retryAfter = body.retryAfter;
  }
}

type JsonBody = Record<string, unknown>;

async function request<T>(method: string, path: string, body?: JsonBody): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      credentials: 'include',
      headers: body ? { 'content-type': 'application/json' } : undefined,
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new ApiError(0, {
      error: {
        message: 'Network error — check your connection.',
        type: 'network',
        code: 'offline',
      },
    });
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let data: unknown = {};
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = {};
    }
  }

  if (!res.ok) throw new ApiError(res.status, data as ApiErrorBody);
  return data as T;
}

export const api = {
  get: <T>(path: string): Promise<T> => request<T>('GET', path),
  post: <T>(path: string, body?: JsonBody): Promise<T> => request<T>('POST', path, body),
  del: <T>(path: string, body?: JsonBody): Promise<T> => request<T>('DELETE', path, body),
};
